// The root route: everything the JSP did server-side, done as a loader (§1.5).
//
// Upstream inlined `window.careero` into the page, so the app had its bootstrap before
// AngularJS started. Here the bootstrap is fetched, which means there IS a loading state
// the old app never had — visible to anything measuring first paint, and the reason the
// baseline capture waits for `$http.pendingRequests` to settle (§7 phase 0.4).
import { useLoaderData, useRouteError } from 'react-router-dom'
import { fetchViewModel, ViewModelError, type ExtensionViewModel } from '@woo-ext/api'
import { extensionTarget } from '../extension-target.ts'

export interface RootData {
  viewModel: ExtensionViewModel
  /** `?target` / `?frameRole`, which the JSP read into window globals (§1.2 job 4). */
  frame: { target: string | null; frameRole: string | null }
}

export async function rootLoader({ request }: { request: Request }): Promise<RootData> {
  const url = new URL(request.url)
  const viewModel = await fetchViewModel()
  // The JSP set `body class="{envMode}"`; ported selectors depend on it, so the class is
  // applied as soon as the bootstrap answers rather than being dropped.
  //
  // `ENV.MODE`, not `env.mode`: careero kept the JSP's own casing so that the DTO merges
  // cleanly over the rendered global in the legacy extension (§4.1). The first version of this
  // read `env.mode`, got undefined, and applied no class at all — invisible until a live dev
  // tenant answered with the real shape.
  const mode = viewModel.ENV?.MODE ?? ''
  if (mode.length > 0) document.body.classList.add(mode.toLowerCase())

  return {
    viewModel,
    frame: {
      target: url.searchParams.get('target'),
      frameRole: url.searchParams.get('frameRole'),
    },
  }
}

export function RootErrorBoundary(): React.JSX.Element {
  const error = useRouteError()
  const isViewModel = error instanceof ViewModelError
  return (
    <main style={{ font: '14px/1.5 system-ui, sans-serif', padding: '2rem' }}>
      <h1>Bootstrap failed</h1>
      <p>
        {isViewModel
          ? `GET /extension/view-model answered ${String(error.status)}.`
          : String(error)}
      </p>
      <p>
        That call goes through the proxy to <code>TENANT_HOST</code>. A per-branch dev tenant needs a hosts
        entry pointing it at the Docker gateway (<code>172.17.0.1</code>), since careero&rsquo;s Traefik listens
        on the Docker host; <code>woo.io</code> works without one.
      </p>
    </main>
  )
}

/**
 * Phase 1's deliverable: prove the bootstrap arrives, through the proxy, authenticated by
 * whatever cookie the browser already holds. Phase 2 replaces this with the real chrome.
 */
export function BootstrapDebugPage(): React.JSX.Element {
  const { viewModel, frame } = useLoaderData() as RootData
  const keys = Object.keys(viewModel).sort()
  // Resolved rather than read straight off the bootstrap: the raw `extensionId` is the
  // legacy extension's, and showing it unqualified is how the trap of §8.1 stays invisible.
  const target = extensionTarget(viewModel)
  const signedIn = viewModel.session !== null

  return (
    <main style={{ font: '14px/1.5 system-ui, sans-serif', padding: '1.5rem', maxWidth: 900 }}>
      <h1 style={{ fontSize: '1.25rem' }}>Bootstrap (GET /extension/view-model)</h1>
      <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.25rem 1rem' }}>
        <dt>session</dt>
        <dd>{signedIn ? 'present — signed in' : 'null — signed out'}</dd>
        <dt>extensionId</dt>
        <dd>
          <code>{String(target.id)}</code> <small>(from {target.source})</small>
          {target.isLegacyFallback ? (
            <>
              {' '}
              <strong>— LEGACY id: this would message the old extension (§8.1)</strong>
            </>
          ) : null}
        </dd>
        <dt>atsName</dt>
        <dd>{String(viewModel.atsName ?? '—')}</dd>
        <dt>fields</dt>
        <dd>{keys.length}</dd>
        <dt>frameRole</dt>
        <dd>{frame.frameRole ?? '—'}</dd>
        <dt>target</dt>
        <dd>{frame.target ?? '—'}</dd>
      </dl>

      <h2 style={{ fontSize: '1rem' }}>Fields the careero-side work adds (plan §4.1)</h2>
      <ul>
        {['ENV', 'extensionFieldTypes', 'extensionPathPerField', 'otherFieldsFieldName', 'cdnUrl', 'baseAppUrl', 'LogUrl'].map(
          (field) => (
            <li key={field}>
              <code>{field}</code> — {field in viewModel ? 'served' : 'not served by this tenant'}
            </li>
          ),
        )}
      </ul>
      <p style={{ fontSize: '0.85rem', color: '#666' }}>
        Absent on a tenant running master; present on one running the extension-support branch. Names keep
        the JSP&rsquo;s casing (<code>ENV</code>, <code>LogUrl</code>) deliberately — see §4.1.
      </p>

      <details>
        <summary>Raw bootstrap</summary>
        <pre style={{ overflow: 'auto', maxHeight: '24rem', background: '#f6f6f6', padding: '0.75rem' }}>
          {JSON.stringify(viewModel, null, 2)}
        </pre>
      </details>
    </main>
  )
}
